<template>
    <div class="admin-settings-goals" data-test-view="goals">
        <l-module-access
            :module="'surveys'"
            :module-access="team.moduleAccess"
            :title="$t(`adminSettings.surveysHeadlineTitle`)"
        ></l-module-access>

        <hr />

        <l-headline
            :title="$t(`adminSettings.surveysTagsHeadlineTitle`)"
            :sub-title="$t(`adminSettings.surveysTagsHeadlineSubtitle`)"
        ></l-headline>

        <draggable v-model="tags" handle=".input-group-addon" @change="changeOrder">
            <transition-group>
                <div
                    v-for="(tag, tagIndex) in tags"
                    :key="tag._id"
                    class="row"
                    data-test-class="team-settings-goals-tags-list-item"
                >
                    <div class="col-xs-12">
                        <div class="form-group">
                            <div class="input-group label-floating">
                                <l-draggable-icon class="input-group-addon" />

                                <l-input-text
                                    v-model="tag.name"
                                    :label="''"
                                    :disabled="!tag.editing"
                                    data-test-class="team-settings-goals-tags-name"
                                    :data-test-id="`team-settings-goals-tags-${tagIndex}-name`"
                                />

                                <span
                                    v-show="tag.editing"
                                    class="input-group-addon clickable color-primary"
                                    data-test-class="team-settings-goals-tags-save"
                                    :data-test-id="`team-settings-goals-tags-${tagIndex}-save`"
                                    tabindex="0"
                                    @click="
                                        () => {
                                            $set(tag, 'editing', false);
                                            updateTag(tag);
                                        }
                                    "
                                    @keyup.enter="
                                        () => {
                                            $set(tag, 'editing', false);
                                            updateTag(tag);
                                        }
                                    "
                                >
                                    {{ $t(`adminSettings.goalsTagsSave`) }}
                                </span>
                                <span
                                    v-show="!tag.editing"
                                    class="input-group-addon clickable color-primary"
                                    data-test-class="team-settings-goals-tags-edit"
                                    :data-test-id="`team-settings-goals-tags-${tagIndex}-edit`"
                                    tabindex="0"
                                    @click="
                                        () => {
                                            $set(tag, 'editing', true);
                                            $forceUpdate();
                                        }
                                    "
                                    @keyup.enter="
                                        () => {
                                            $set(tag, 'editing', true);
                                            $forceUpdate();
                                        }
                                    "
                                >
                                    {{ $t(`adminSettings.goalsTagsEdit`) }}
                                </span>
                                <span
                                    class="input-group-addon clickable color-warning"
                                    data-test-class="team-settings-goals-tags-remove"
                                    :data-test-id="`team-settings-goals-tags-${tagIndex}-remove`"
                                    tabindex="0"
                                    @click="
                                        () => {
                                            tag.status = 'removed';
                                            updateTag(tag);
                                        }
                                    "
                                    @keyup.enter="
                                        () => {
                                            tag.status = 'removed';
                                            updateTag(tag);
                                        }
                                    "
                                >
                                    {{ $t(`adminSettings.goalsTagsRemove`) }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </transition-group>
        </draggable>

        <div class="row">
            <div class="col-xs-12">
                <div class="form-group">
                    <div class="input-group label-floating">
                        <l-input-text
                            v-model="newTagName"
                            :optional="true"
                            :label="''"
                            :placeholder="$t(`adminSettings.goalsTagsCreatePlaceholder`)"
                            :hide-help-block="true"
                            data-test-id="team-settings-goals-tags-new"
                        />
                        <span
                            class="input-group-addon clickable color-primary l-default-margin"
                            data-test-id="team-settings-goals-tags-new-create"
                            tabindex="0"
                            @click="
                                () => {
                                    updateTag({
                                        name: newTagName,
                                        status: 'active',
                                        order: tags.length
                                            ? (tags[tags.length - 1].order || tags.length) + 1
                                            : 0,
                                    });
                                    newTagName = '';
                                }
                            "
                            @keyup.enter="
                                () => {
                                    updateTag({
                                        name: newTagName,
                                        status: 'active',
                                        order: tags.length
                                            ? (tags[tags.length - 1].order || tags.length) + 1
                                            : 0,
                                    });
                                    newTagName = '';
                                }
                            "
                        >
                            {{ $t(`adminSettings.goalsTagsCreateBtn`) }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <hr />
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import Draggable from "vuedraggable";

import LDraggableIcon from "@/components/LDraggableIcon";
import LHeadline from "@/components/LHeadline";
import LInputText from "@/components/LInputText";
import LModuleAccess from "@/components/LModuleAccess";

export default {
    components: {
        Draggable,
        LDraggableIcon,
        LHeadline,
        LInputText,
        LModuleAccess,
    },
    data() {
        return {
            team: {
                settings: {},
            },
            updateSuccess: false,
            updateError: false,
            newTagName: "",
            uploadGoalInProgress: false,
            uploadGoalResultsSuccess: false,
            uploadGoalResultsError: false,
        };
    },
    computed: {
        ...mapGetters({
            rights: "users/current/rights",
            getTeamInfo: "teams/current/info",
            tags: "tags/tags/surveys",
        }),
    },

    created() {
        this.team = this.getTeamInfo();
    },
    methods: {
        updateTag({ _id, name, status, order }) {
            if (!name) {
                return;
            }

            this.$store.dispatch({
                type: "tags/tags/update",
                payload: {
                    data: { _id, name, status, order, type: "surveys" },
                },
            });
        },
        changeOrder(x) {
            const tags = [];
            tags.push(this.tags[x.moved.newIndex]);
            tags.push(this.tags[x.moved.oldIndex]);
            [tags[0].order, tags[1].order] = [tags[1].order, tags[0].order];
            this.tags[x.moved.oldIndex].order;
            this.$store.dispatch({
                type: "tags/tags/updateOrder",
                payload: {
                    data: tags,
                },
            });
        },
    },
};
</script>

<style scoped>
.admin-settings-goals-import-form .btn i {
    margin-right: 8px;
}
</style>
